import { useCallback, useEffect, useRef, useState } from 'react';
import { POS_SCANNER_BRIDGE_SSE_URL } from '../pages/catalog/useHidBarcodeWedge';

const HEALTH_URL = 'http://127.0.0.1:3921/health';
const HEALTH_POLL_MS = 5_000;

export type ScannerBridgeStatus = 'offline' | 'bridge' | 'ready';

export type ScannerBridgeState = {
  status: ScannerBridgeStatus;
  detail: string | null;
  lastCode: string | null;
  lastScanAt: number | null;
};

type HealthPayload = {
  ok?: boolean;
  detail?: string;
  last_scan?: string | null;
};

/**
 * Shell-level listener for `scripts/pos-scanner-bridge.py`.
 * SSE for scans + /health poll for USB/bridge presence.
 */
export function usePosScannerBridge(opts: {
  enabled: boolean;
  onScan?: (code: string) => void;
}): ScannerBridgeState {
  const [status, setStatus] = useState<ScannerBridgeStatus>('offline');
  const [detail, setDetail] = useState<string | null>(null);
  const [lastCode, setLastCode] = useState<string | null>(null);
  const [lastScanAt, setLastScanAt] = useState<number | null>(null);
  const onScanRef = useRef(opts.onScan);
  onScanRef.current = opts.onScan;
  const lastEmittedRef = useRef<{ code: string; at: number } | null>(null);

  const emitScan = useCallback((raw: string) => {
    const code = raw.trim().replace(/\D/g, '') || raw.trim();
    if (!code) return;
    const now = Date.now();
    const prev = lastEmittedRef.current;
    // Dedupe bridge + wedge double-fire within 400ms.
    if (prev && prev.code === code && now - prev.at < 400) return;
    lastEmittedRef.current = { code, at: now };
    setLastCode(code);
    setLastScanAt(now);
    onScanRef.current?.(code);
  }, []);

  useEffect(() => {
    if (!opts.enabled) {
      setStatus('offline');
      setDetail(null);
      return;
    }

    let cancelled = false;
    let es: EventSource | null = null;
    let reconnectTimer: number | null = null;
    let healthTimer: number | null = null;
    let attempt = 0;
    let streamOpen = false;
    let usbOk = false;
    let healthDetail: string | null = null;

    const publish = () => {
      if (cancelled) return;
      setDetail(healthDetail);
      if (!streamOpen) {
        setStatus('offline');
        return;
      }
      setStatus(usbOk ? 'ready' : 'bridge');
    };

    const pollHealth = () => {
      void fetch(HEALTH_URL, { cache: 'no-store' })
        .then(async (r) => {
          const body = (await r.json().catch(() => ({}))) as HealthPayload;
          usbOk = Boolean(body.ok);
          healthDetail = typeof body.detail === 'string' ? body.detail : null;
          if (typeof body.last_scan === 'string' && body.last_scan.trim()) {
            setLastCode((prev) => prev ?? body.last_scan!.trim());
          }
          publish();
        })
        .catch(() => {
          usbOk = false;
          healthDetail = streamOpen ? 'bridge sans USB' : null;
          publish();
        });
    };

    const connect = () => {
      if (cancelled) return;
      es = new EventSource(POS_SCANNER_BRIDGE_SSE_URL);
      es.onopen = () => {
        attempt = 0;
        streamOpen = true;
        publish();
        pollHealth();
      };
      es.onmessage = (ev) => {
        const code = ev.data?.trim();
        if (code) emitScan(code);
      };
      es.onerror = () => {
        streamOpen = false;
        publish();
        es?.close();
        es = null;
        if (cancelled) return;
        attempt += 1;
        const delay = Math.min(30_000, 1_000 * 2 ** Math.min(attempt, 5));
        reconnectTimer = window.setTimeout(connect, delay);
      };
    };

    connect();
    pollHealth();
    healthTimer = window.setInterval(pollHealth, HEALTH_POLL_MS);

    return () => {
      cancelled = true;
      es?.close();
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      if (healthTimer) window.clearInterval(healthTimer);
    };
  }, [opts.enabled, emitScan]);

  return { status, detail, lastCode, lastScanAt };
}
